import React, { useEffect, useState, lazy, } from "react";
import { FormattedMessage, useIntl, } from "react-intl";
import { useForm, Controller, } from "react-hook-form";
import { Card, notification, ColorPicker, Descriptions, Button, Image, Form, Input, Switch, } from "antd";
import { Info, Pen, CheckSquare, } from "@phosphor-icons/react";
import { UpdateCustomerBrandingSettings, CustomerSettings, } from '../../../services/settings';
import PlaceHolder from '../../../assets/images/placeholder.png';
import validator from 'validator';

const SettingsFinanceView = lazy(() => import('./finance/settings.finance.view'));
const SettingsFinanceInvoiceView = lazy(() => import("../views/finance/settings.finance.invoice.view"));
const SettingsFinancePaymentView = lazy(() => import("../views/finance/settings.finance.payment.view"));
const SettingsImageModal = lazy(() => import('../modal/settings.image.modal'));

const SettingsCustomerBasicView = lazy(() => import('../views/customer/settings.customer.basic.view'));

// This view is for ADMIN only. It will mount into:
// - the CustomerDetailPage in the tab view.
// - the ProfileDetailPage in the tab view.

const SettingsCustomerBrandingView = ({
    customerID,
    history,
    ...props
}) => {

    const [loadingData, setLoadingData] = useState(true);
    const [formDisabled, setFormDisabled] = useState(true);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [accountancyPluginEnabled, setAccountancyPluginEnabled] = useState(false);
    const [generalJournalSupported, setGeneralJournalSupported] = useState(false);
    const [directDebitPluginEnabled, setDirectDebitPluginEnabled] = useState(false);
    const [brandingSettings, setBrandingSettings] = useState({});
    const [imageType, setImageType] = useState(undefined);
    const { permissionTemplate: { finance_billing, } = {} } = props;
    const { handleSubmit, reset, control, setValue, watch, } = useForm({ mode: "all" }) // set the field validation mode);
    const intl = useIntl();

    useEffect(() => {
        if (customerID) {
            GetCustomerSettings();
        }
        // eslint-disable-next-line
    }, [customerID]);

    useEffect(() => {
        reset({
            branding_color_header: brandingSettings.branding_color_header,
            branding_color_primary: brandingSettings.branding_color_primary,
            branding_color_hover: brandingSettings.branding_color_hover,
            branding_name: brandingSettings.branding_name,
            branding_phone: brandingSettings.branding_phone,
            branding_slogan: brandingSettings.branding_slogan,
            branding_google_maps: brandingSettings.branding_google_maps,
            branding_remove_banner: brandingSettings.branding_remove_banner,
            branding_disable_shop: brandingSettings.branding_disable_shop,
            facebook_enable: brandingSettings.branding_socials?.facebook_enable,
            facebook_link: brandingSettings.branding_socials?.facebook_link,
            instagram_enable: brandingSettings.branding_socials?.instagram_enable,
            instagram_link: brandingSettings.branding_socials?.instagram_link,
            twitter_enable: brandingSettings.branding_socials?.twitter_enable,
            twitter_link: brandingSettings.branding_socials?.twitter_link,
            linkedin_enable: brandingSettings.branding_socials?.linkedin_enable,
            linkedin_link: brandingSettings.branding_socials?.linkedin_link,
            youtube_enable: brandingSettings.branding_socials?.youtube_enable,
            youtube_link: brandingSettings.branding_socials?.youtube_link,

            // remove the @${process.env.REACT_APP_SUBDOMAIN}`
            forward_support_email: brandingSettings.forward_support_email?.replace(`@${process.env.REACT_APP_SUBDOMAIN}`, ''),
        });

        // eslint-disable-next-line
    }, [brandingSettings]);

    // Checking plugin status for finance and direct debit
    useEffect(() => {
        // check if plugin and deposit memoriaal (general journal) has supported
        for (let index = 0; index < brandingSettings.settings_plugin_data?.ACCOUNTANCY?.length; index++) {
            const element = brandingSettings.settings_plugin_data?.ACCOUNTANCY[index];

            // if there is an accountant plugin enabled
            if (element.plugin_enabled) {
                setAccountancyPluginEnabled(true);
            }

            if (element.plugin_key === 'exactonline' && element.plugin_enabled) {
                setGeneralJournalSupported(true);
                break;
            }
        }

        // Check if the direct debit is enabled
        for (let index = 0; index < brandingSettings.settings_plugin_data?.DIRECT_DEBIT?.length; index++) {
            const element = brandingSettings.settings_plugin_data?.DIRECT_DEBIT[index];
            // Is plugin enabled
            if (element.plugin_enabled) {
                setDirectDebitPluginEnabled(true);
            }
        }

        // eslint-disable-next-line
    }, [brandingSettings]);

    // useEffect(() => {
    //     // update settingsData?.branding_financial?.general_journal_deposit_enabled if generalJournalSupported is false
    //     if (!loadingData) {
    //         if (!generalJournalSupported) {
    //             setBrandingSettings(prevSettings => ({
    //                 ...prevSettings,
    //                 branding_financial: {
    //                     ...prevSettings.branding_financial,
    //                     general_journal_deposit_enabled: false
    //                 }
    //             }));
    //         }
    //     }
    // }, [generalJournalSupported, loadingData]);

    const GetCustomerSettings = () => {
        setLoadingData(true);
        CustomerSettings(customerID, history)
            .then(response => {
                setBrandingSettings(
                    {
                        customer_id: response.data.customer_id,
                        vendor_id: response.data.vendor_id,
                        hostname: response.data.hostname,
                        admin_hostname: response.data.admin_hostname,
                        customer_number_sequence: response.data.customer_number_sequence,
                        // settings_invoice: response.data.settings_invoice,
                        settings_infra: response.data.settings_infra,
                        ...response.data.settings_branding
                    }, setLoadingData(false));
            })
            .catch(error => {
                notification['error']({
                    message: 'Customer settings',
                    description: intl.formatMessage({ id: "UNKNOWN_SYSTEM_ERROR" }),
                });
                history('/dashboard');
            });
    };

    const saveSettings = (data) => {
        setButtonDisabled(true);

        const branding_socials = {
            facebook_enable: data.facebook_enable,
            facebook_link: data.facebook_link,
            instagram_enable: data.instagram_enable,
            instagram_link: data.instagram_link,
            twitter_enable: data.twitter_enable,
            twitter_link: data.twitter_link,
            linkedin_enable: data.linkedin_enable,
            linkedin_link: data.linkedin_link,
            youtube_enable: data.youtube_enable,
            youtube_link: data.youtube_link,
        };

        const post_data = {
            customer_id: brandingSettings.customer_id,
            // branding_financial: { ...brandingSettings.branding_financial },
            // settings_invoice: { ...brandingSettings.settings_invoice },
            ...brandingSettings,
            ...data,
            branding_socials,
        };

        post_data.forward_support_email = `${data.forward_support_email}@${process.env.REACT_APP_SUBDOMAIN}`;

        UpdateCustomerBrandingSettings(post_data, history)
            .then(response => {
                if (response.status) {
                    setFormDisabled(true);
                    notification['success']({
                        message: 'Branding settings',
                        description: intl.formatMessage({ id: response.msg }),
                    });
                    GetCustomerSettings();
                } else {
                    notification['error']({
                        message: 'Branding settings',
                        description: intl.formatMessage({ id: response.msg }),
                    });
                }
                setButtonDisabled(false);

            })
            .catch(error => {
                setButtonDisabled(false);
            })
    };

    const editSettings = () => {
        setFormDisabled(!formDisabled);
    };

    const editImageModalOpen = (imagetype) => {
        setImageType(imagetype);
        setImageModalOpen(true);
    };

    const presetColors = [
        {
            label: 'Recommended',
            colors: [
                '#000000',
                '#000000E0',
                '#000000A6',
                '#00000073',
                '#00000040',
                '#00000026',
                '#0000001A',
                '#00000012',
                '#0000000A',
                '#00000005',
                '#F5222D',
                '#FA8C16',
                '#FADB14',
                '#8BBB11',
                '#52C41A',
                '#13A8A8',
                '#1677FF',
                '#2F54EB',
                '#722ED1',
                '#EB2F96',
                '#F5222D4D',
                '#FA8C164D',
                '#FADB144D',
                '#8BBB114D',
                '#52C41A4D',
                '#13A8A84D',
                '#1677FF4D',
                '#2F54EB4D',
                '#722ED14D',
                '#EB2F964D',
            ],
        },
        {
            label: 'Recent',
            colors: [],
        },
    ];

    return (
        <React.Fragment>

            {/* Customer branding settings */}
            <Card
                title={<><h3 className="card-title"><FormattedMessage id="setting-branding" /></h3>
                    <small className="font-weight-light">Manage display settings for the webshop.</small></>}
                className="my-4"
                loading={loadingData}
                extra={[
                    <React.Fragment key="0">
                        {formDisabled ? (
                            <Button
                                onClick={() => { editSettings() }}
                                icon={<Pen size={16} weight="light" />}>
                                <FormattedMessage id="settings-edit" />
                            </Button>
                        ) : (
                            <Button
                                disabled={buttonDisabled}
                                loading={buttonDisabled}
                                onClick={handleSubmit(saveSettings)}
                                className="ant-btn-success">
                                {!buttonDisabled && <CheckSquare size={16} weight="light" className="mr-1" />}
                                <FormattedMessage id="settings-save" />
                            </Button>
                        )}
                    </React.Fragment>
                ]}>

                <Descriptions bordered column={1}>

                    {/* Company name */}
                    <Descriptions.Item label={<FormattedMessage id="branding-name" />}>
                        <FormattedMessage id="branding-name">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue=""
                                    name="branding_name"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Input
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-name-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Company phone */}
                    <Descriptions.Item label={<FormattedMessage id="branding-phone" />}>
                        <FormattedMessage id="branding-phone">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue=""
                                    name="branding_phone"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Input
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-phone-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Company mail */}
                    <Descriptions.Item label={<FormattedMessage id="branding-email" />}>
                        <FormattedMessage id="branding-email">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue=""
                                    name="branding_email"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Input
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-email-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Company slogan */}
                    <Descriptions.Item label={<FormattedMessage id="branding-slogan" />}>
                        <FormattedMessage id="branding-slogan">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue=""
                                    name="branding_slogan"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Input
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-slogan-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Company branding_google_maps */}
                    <Descriptions.Item label={<FormattedMessage id="branding-google-maps" />}>
                        <FormattedMessage id="branding-google-maps">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue=""
                                    name="branding_google_maps"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Input
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-google-maps-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* forward_support_email */}
                    <Descriptions.Item label={<FormattedMessage id="support-mail-address" />}>
                        <FormattedMessage id="support-mail-address">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue=""
                                    name="forward_support_email"
                                    rules={{
                                        pattern: {
                                            // value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,24}$/i,
                                            value: /^[a-zA-Z0-9.-]*$/i,
                                            message: "EmailAddressNotValid",
                                        },
                                    }}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Input
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled}
                                                addonAfter={`@${process.env.REACT_APP_SUBDOMAIN}`}
                                            />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="support-mail-address-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* branding_socials */}

                    {/* Instagram enable */}
                    <Descriptions.Item label={<FormattedMessage id="branding-instagram" />}>
                        <FormattedMessage id="branding-instagram">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue={false}
                                    name="instagram_enable"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Switch
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="instagram-enable-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Instagram link */}
                    {watch("instagram_enable") ? (
                        <Descriptions.Item label={<FormattedMessage id="branding-instagram-link" />}>
                            <FormattedMessage id="branding-instagram-link">
                                {NameField =>
                                    <Controller
                                        control={control}
                                        defaultValue=""
                                        name="instagram_link"
                                        rules={{
                                            // required: { value: true, message: "inputfield-FieldIsRequired" },
                                            validate: (value) => validator.isURL(value) || "inputfield-NotAvalidURL",
                                        }}
                                        render={({
                                            field: { onChange, onBlur, value, name, ref },
                                            fieldState: { error },
                                        }) => (
                                            <Form.Item
                                                help={error && intl.formatMessage({ id: error?.message })}
                                                hasFeedback
                                                validateStatus={error?.message && "error"}>
                                                <Input
                                                    placeholder={NameField}
                                                    onChange={onChange}
                                                    value={value}
                                                    readOnly={formDisabled}
                                                    disabled={formDisabled} />
                                            </Form.Item>
                                        )} />}
                            </FormattedMessage>
                            {!formDisabled && (
                                <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="instagram-link-tt" /></small>
                            )}
                        </Descriptions.Item>
                    ) : (<React.Fragment></React.Fragment>)}

                    {/* Facebook enable */}
                    <Descriptions.Item label={<FormattedMessage id="branding-facebook" />}>
                        <FormattedMessage id="branding-facebook">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue={false}
                                    name="facebook_enable"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Switch
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="facebook-enable-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* facebook link */}
                    {watch("facebook_enable") ? (
                        <Descriptions.Item label={<FormattedMessage id="branding-facebook-link" />}>
                            <FormattedMessage id="branding-facebook-link">
                                {NameField =>
                                    <Controller
                                        control={control}
                                        defaultValue=""
                                        name="facebook_link"
                                        rules={{
                                            // required: { value: true, message: "inputfield-FieldIsRequired" },
                                            validate: (value) => validator.isURL(value) || "inputfield-NotAvalidURL",
                                        }}
                                        render={({
                                            field: { onChange, onBlur, value, name, ref },
                                            fieldState: { error },
                                        }) => (
                                            <Form.Item
                                                help={error && intl.formatMessage({ id: error?.message })}
                                                hasFeedback
                                                validateStatus={error?.message && "error"}>
                                                <Input
                                                    placeholder={NameField}
                                                    onChange={onChange}
                                                    value={value}
                                                    readOnly={formDisabled}
                                                    disabled={formDisabled} />
                                            </Form.Item>
                                        )} />}
                            </FormattedMessage>
                            {!formDisabled && (
                                <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="facebook-link-tt" /></small>
                            )}
                        </Descriptions.Item>
                    ) : (<React.Fragment></React.Fragment>)}

                    {/* twitter enable */}
                    <Descriptions.Item label={<FormattedMessage id="branding-twitter" />}>
                        <FormattedMessage id="branding-twitter">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue={false}
                                    name="twitter_enable"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Switch
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="twitter-enable-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* twitter link */}
                    {watch("twitter_enable") ? (
                        <Descriptions.Item label={<FormattedMessage id="branding-twitter-link" />}>
                            <FormattedMessage id="branding-twitter-link">
                                {NameField =>
                                    <Controller
                                        control={control}
                                        defaultValue=""
                                        name="twitter_link"
                                        rules={{
                                            // required: { value: true, message: "inputfield-FieldIsRequired" },
                                            validate: (value) => validator.isURL(value) || "inputfield-NotAvalidURL",
                                        }}
                                        render={({
                                            field: { onChange, onBlur, value, name, ref },
                                            fieldState: { error },
                                        }) => (
                                            <Form.Item
                                                help={error && intl.formatMessage({ id: error?.message })}
                                                hasFeedback
                                                validateStatus={error?.message && "error"}>
                                                <Input
                                                    placeholder={NameField}
                                                    onChange={onChange}
                                                    value={value}
                                                    readOnly={formDisabled}
                                                    disabled={formDisabled} />
                                            </Form.Item>
                                        )} />}
                            </FormattedMessage>
                            {!formDisabled && (
                                <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="twitter-link-tt" /></small>
                            )}
                        </Descriptions.Item>
                    ) : (<React.Fragment></React.Fragment>)}

                    {/* linkedin enable */}
                    <Descriptions.Item label={<FormattedMessage id="branding-linkedin" />}>
                        <FormattedMessage id="branding-linkedin">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue={false}
                                    name="linkedin_enable"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Switch
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="linkedin-enable-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* linkedin link */}
                    {watch("linkedin_enable") ? (
                        <Descriptions.Item label={<FormattedMessage id="branding-linkedin-link" />}>
                            <FormattedMessage id="branding-linkedin-link">
                                {NameField =>
                                    <Controller
                                        control={control}
                                        defaultValue=""
                                        name="linkedin_link"
                                        rules={{
                                            // required: { value: true, message: "inputfield-FieldIsRequired" },
                                            validate: (value) => validator.isURL(value) || "inputfield-NotAvalidURL",
                                        }}
                                        render={({
                                            field: { onChange, onBlur, value, name, ref },
                                            fieldState: { error },
                                        }) => (
                                            <Form.Item
                                                help={error && intl.formatMessage({ id: error?.message })}
                                                hasFeedback
                                                validateStatus={error?.message && "error"}>
                                                <Input
                                                    placeholder={NameField}
                                                    onChange={onChange}
                                                    value={value}
                                                    readOnly={formDisabled}
                                                    disabled={formDisabled} />
                                            </Form.Item>
                                        )} />}
                            </FormattedMessage>
                            {!formDisabled && (
                                <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="linkedin-link-tt" /></small>
                            )}
                        </Descriptions.Item>
                    ) : (<React.Fragment></React.Fragment>)}

                    {/* youtube enable */}
                    <Descriptions.Item label={<FormattedMessage id="branding-youtube" />}>
                        <FormattedMessage id="branding-youtube">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue={false}
                                    name="youtube_enable"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Switch
                                                placeholder={NameField}
                                                onChange={onChange}
                                                value={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="youtube-enable-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* youtube link */}
                    {watch("youtube_enable") ? (
                        <Descriptions.Item label={<FormattedMessage id="branding-youtube-link" />}>
                            <FormattedMessage id="branding-youtube-link">
                                {NameField =>
                                    <Controller
                                        control={control}
                                        defaultValue=""
                                        name="youtube_link"
                                        rules={{
                                            // required: { value: true, message: "inputfield-FieldIsRequired" },
                                            validate: (value) => validator.isURL(value) || "inputfield-NotAvalidURL",
                                        }}
                                        render={({
                                            field: { onChange, onBlur, value, name, ref },
                                            fieldState: { error },
                                        }) => (
                                            <Form.Item
                                                help={error && intl.formatMessage({ id: error?.message })}
                                                hasFeedback
                                                validateStatus={error?.message && "error"}>
                                                <Input
                                                    placeholder={NameField}
                                                    onChange={onChange}
                                                    value={value}
                                                    readOnly={formDisabled}
                                                    disabled={formDisabled} />
                                            </Form.Item>
                                        )} />}
                            </FormattedMessage>
                            {!formDisabled && (
                                <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="youtube-link-tt" /></small>
                            )}
                        </Descriptions.Item>
                    ) : (<React.Fragment></React.Fragment>)}

                    {/* Company logo */}
                    <Descriptions.Item label={<FormattedMessage id="branding-logo" />}>
                        <Image
                            style={{ objectFit: "scale-down", height: "150px" }}
                            src={brandingSettings?.branding_logo ? `https://${process.env.REACT_APP_AWS_DATASTORE}/${brandingSettings?.branding_logo}` : PlaceHolder}
                            preview={false} />
                        <br />
                        <Button size="small"
                            onClick={e => editImageModalOpen("branding_logo")} className="mt-1"
                            icon={<Pen size={16} weight="light" />}>
                            Bewerken</Button>
                    </Descriptions.Item>

                    {/* Homepage header image */}
                    <Descriptions.Item label={<FormattedMessage id="homepage-image" />}>
                        <Image
                            style={{ objectFit: "scale-down", height: "150px" }}
                            src={brandingSettings?.branding_homepage_image ? `https://${process.env.REACT_APP_AWS_DATASTORE}/${brandingSettings?.branding_homepage_image}` : PlaceHolder}
                            preview={false} />
                        <br />
                        <Button size="small"
                            onClick={e => editImageModalOpen("branding_homepage_image")} className="mt-1"
                            icon={<Pen size={16} weight="light" />}>
                            Bewerken</Button>
                    </Descriptions.Item>

                    {/* Homepage office image */}
                    <Descriptions.Item label={<FormattedMessage id="homepage-office-image" />}>
                        <Image
                            style={{ objectFit: "scale-down", height: "150px" }}
                            src={brandingSettings?.branding_office_image ? `https://${process.env.REACT_APP_AWS_DATASTORE}/${brandingSettings?.branding_office_image}` : PlaceHolder}
                            preview={false} />
                        <br />
                        <Button size="small"
                            onClick={e => editImageModalOpen("branding_office_image")} className="mt-1"
                            icon={<Pen size={16} weight="light" />}>
                            Bewerken</Button>
                    </Descriptions.Item>

                    {/* Header page image */}
                    <Descriptions.Item label={<FormattedMessage id="header-page-image" />}>
                        <Image
                            style={{ objectFit: "scale-down", height: "150px" }}
                            src={brandingSettings?.branding_page_image ? `https://${process.env.REACT_APP_AWS_DATASTORE}/${brandingSettings?.branding_page_image}` : PlaceHolder}
                            preview={false} />
                        <br />
                        <Button
                            size="small"
                            onClick={e => editImageModalOpen("branding_page_image")}
                            className="mt-1"
                            icon={<Pen size={16} weight="light" />}>
                            Bewerken</Button>
                    </Descriptions.Item>

                    {/* Color header */}
                    <Descriptions.Item label={<FormattedMessage id="branding-header-color" />}>
                        <FormattedMessage id="branding-header-color">
                            {NameField =>
                                <Controller
                                    control={control}
                                    // defaultValue={colors.branding_color_header}
                                    name="branding_color_header"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <ColorPicker
                                                value={value}
                                                allowClear={true}
                                                disabled={formDisabled}
                                                onChange={e => {
                                                    setValue('branding_color_header', e.metaColor.toRgbString());
                                                }}
                                                presets={presetColors} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-header-color-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Color primary */}
                    <Descriptions.Item label={<FormattedMessage id="branding-primary-color" />}>
                        <FormattedMessage id="branding-primary-color">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue=""
                                    name="branding_color_primary"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <ColorPicker
                                                value={value}
                                                allowClear={true}
                                                disabled={formDisabled}
                                                onChange={e => setValue('branding_color_primary', e.metaColor.toRgbString())}
                                                presets={presetColors} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-primary-color-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Color hover */}
                    <Descriptions.Item label={<FormattedMessage id="branding-hover-color" />}>
                        <FormattedMessage id="branding-hover-color">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue=""
                                    name="branding_color_hover"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <ColorPicker
                                                disabled={formDisabled}
                                                allowClear={true}
                                                value={value}
                                                onChange={e => setValue('branding_color_hover', e.metaColor.toRgbString())}
                                                presets={presetColors} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-hover-color-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* remove powered by banner */}
                    <Descriptions.Item label={<FormattedMessage id="branding-remove-banner" />}>
                        <FormattedMessage id="branding-remove-banner">
                            {NameField =>
                                <Controller
                                    control={control}
                                    defaultValue={false}
                                    name="branding_remove_banner"
                                    rules={{}}
                                    render={({
                                        field: { onChange, onBlur, value, name, ref },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Item
                                            help={error && intl.formatMessage({ id: error?.message })}
                                            hasFeedback
                                            validateStatus={error?.message && "error"}>
                                            <Switch
                                                onChange={onChange}
                                                checked={value}
                                                readOnly={formDisabled}
                                                disabled={formDisabled} />
                                        </Form.Item>
                                    )} />}
                        </FormattedMessage>
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-remove-banner-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Disable webshop */}
                    <Descriptions.Item label={<FormattedMessage id="branding-disable-shop" />}>
                        <Controller
                            control={control}
                            defaultValue={false}
                            name="branding_disable_shop"
                            rules={{}}
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { error },
                            }) => (
                                <Form.Item
                                    help={error && intl.formatMessage({ id: error?.message })}
                                    hasFeedback
                                    validateStatus={error?.message && "error"}>
                                    <Switch
                                        onChange={onChange}
                                        checked={value}
                                        readOnly={formDisabled}
                                        disabled={formDisabled} />
                                </Form.Item>
                            )} />
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-disable-shop-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Primair rental / webshop usaged */}
                    <Descriptions.Item label={<FormattedMessage id="branding-webshop-disable-rental" />}>
                        <Controller
                            control={control}
                            defaultValue={false}
                            name="branding_webshop_disable_rental"
                            rules={{}}
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { error },
                            }) => (
                                <Form.Item
                                    help={error && intl.formatMessage({ id: error?.message })}
                                    hasFeedback
                                    validateStatus={error?.message && "error"}>
                                    <Switch
                                        onChange={onChange}
                                        checked={value}
                                        readOnly={formDisabled}
                                        disabled={formDisabled} />
                                </Form.Item>
                            )} />
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-webshop-disable-rental-tt" /></small>
                        )}
                    </Descriptions.Item>

                    {/* Enable the product locations in webshop */}
                    <Descriptions.Item label={<FormattedMessage id="branding-enable-product-locations" />}>
                        <Controller
                            control={control}
                            defaultValue={false}
                            name="branding_enable_product_locations"
                            rules={{}}
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { error },
                            }) => (
                                <Form.Item
                                    help={error && intl.formatMessage({ id: error?.message })}
                                    hasFeedback
                                    validateStatus={error?.message && "error"}>
                                    <Switch
                                        onChange={onChange}
                                        checked={value}
                                        readOnly={formDisabled}
                                        disabled={formDisabled} />
                                </Form.Item>
                            )} />
                        {!formDisabled && (
                            <small className="form-text text-muted"><Info size={14} weight="light" /> <FormattedMessage id="branding-enable-product-locations-tt" /></small>
                        )}
                    </Descriptions.Item>

                </Descriptions>
            </Card>

            {/* Customer settings */}
            <SettingsCustomerBasicView
                refreshData={GetCustomerSettings}
                settingsData={brandingSettings}
                history={history}
                {...props}
            />

            {/* Financial basic settings */}
            <SettingsFinanceView
                refreshData={GetCustomerSettings}
                history={history}
                settingsData={brandingSettings}
                accountancyPluginEnabled={accountancyPluginEnabled}
                directDebitPluginEnabled={directDebitPluginEnabled}
                generalJournalSupported={generalJournalSupported}
                {...props}
            />

            {/* Invoice / Billing settings */}
            {finance_billing && (
                <React.Fragment>
                    <SettingsFinanceInvoiceView
                        settingsData={brandingSettings}
                        history={history}
                        refreshData={GetCustomerSettings}
                        accountancyPluginEnabled={accountancyPluginEnabled}
                        directDebitPluginEnabled={directDebitPluginEnabled}
                        generalJournalSupported={generalJournalSupported}
                        {...props} />

                    {/* Payment settings */}
                    <SettingsFinancePaymentView
                        settingsData={brandingSettings}
                        history={history}
                        refreshData={GetCustomerSettings}
                        accountancyPluginEnabled={accountancyPluginEnabled}
                        directDebitPluginEnabled={directDebitPluginEnabled}
                        generalJournalSupported={generalJournalSupported}
                        {...props} />
                </React.Fragment>
            )}

            {imageModalOpen && (
                <SettingsImageModal
                    modalIsOpen={imageModalOpen}
                    modalIsOpenHandler={setImageModalOpen}
                    refreshData={GetCustomerSettings}
                    imageData={brandingSettings}
                    imageType={imageType}
                    customerID={brandingSettings.customer_id}
                />
            )}


        </React.Fragment>
    );

};

export default SettingsCustomerBrandingView;
